import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import { resolveSymbolView } from "../../view/grid/SymbolView";
import type { SymbolLandEffect, SymbolLandAnimationRequest } from "../symbol-land/SymbolLandEffect";
import type { SymbolWinEffect, SymbolWinAnimationRequest } from "../symbol-win/SymbolWinEffect";
import type { SymbolRemovalEffect, SymbolRemovalAnimationRequest } from "../symbol-removal/SymbolRemovalEffect";
import type { SymbolSpineHost } from "./SymbolSpineHost";

/**
 * Adapters that drive {@link SymbolSpineHost} through the three effect ports.
 *
 * Each one is deliberately thin: the host owns the skeleton, the seating, the sprite, the mix
 * table, the time scale and the teardown, so an adapter only carries the bookkeeping its OWN port
 * requires. A cell whose symbol has no skeleton, or whose skeleton lacks the clip, lands plain, and
 * a removal it cannot play reports `null` so the mechanic runs its fallback.
 */

/**
 * LAND (`in`): plays on the host, then rolls into the idle loop on the same skeleton. The view is
 * the grid's cell from the moment the reels stop, so the landing and the idle play on one view with
 * no swap between them — nothing to track, nothing to finish (user, 2026-09-26).
 */
export class HostedSpineLandEffect implements SymbolLandEffect {
  private readonly host: SymbolSpineHost;
  private readonly clipName: string;
  /** Symbols that land plain. The host skips their pre-land pose. */
  private readonly skipSymbolIds: ReadonlySet<string>;

  public constructor(input: { host: SymbolSpineHost; clipName: string; skipSymbolIds?: readonly string[] | null }) {
    this.host = input.host;
    this.clipName = input.clipName;
    this.skipSymbolIds = new Set(input.skipSymbolIds ?? []);
  }

  public play(request: SymbolLandAnimationRequest): void {
    if (this.skipSymbolIds.has(request.symbolId)) return;
    const view = resolveSymbolView(request.cellNode);
    if (view) this.host.play(view, request.symbolId, this.clipName, { queueIdleAfter: true });
  }

  /** The clip already ends in the idle on the view the grid keeps: there is no rest pose to restore. */
  public finishAll(): void {}
}

/** WIN (`win`): plays on the host, then resumes idle so the winner lives until it's removed. */
export class HostedSpineWinEffect implements SymbolWinEffect {
  private readonly host: SymbolSpineHost;
  private readonly clipName: string;

  public constructor(input: { host: SymbolSpineHost; clipName: string }) {
    this.host = input.host;
    this.clipName = input.clipName;
  }

  public play(request: SymbolWinAnimationRequest): void {
    const view = resolveSymbolView(request.cellNode);
    if (!view) return;
    this.host.play(view, request.symbolId, this.clipName, { queueIdleAfter: true });
  }

  /** Reported so the highlight can hold its beat until the winners have actually finished. */
  public getPlaySeconds(symbolId: string): number {
    return Math.max(0, this.host.getClipSeconds(symbolId, this.clipName));
  }

  /** Nothing to unwind: the win rolled into the idle loop, and the host is reclaimed by the pool release hook. */
  public stopAll(_opts?: { revert?: boolean }): void {
    /* no-op by construction */
  }
}

/** REMOVAL (`out`): plays on the host and resolves when the destruction clip completes. */
export class HostedSpineRemovalEffect implements SymbolRemovalEffect {
  private readonly host: SymbolSpineHost;
  private readonly clipName: string;
  private readonly taskRunner: PresentationTaskRunner;

  public constructor(input: { host: SymbolSpineHost; clipName: string; taskRunner: PresentationTaskRunner }) {
    this.host = input.host;
    this.clipName = input.clipName;
    this.taskRunner = input.taskRunner;
  }

  public play(request: SymbolRemovalAnimationRequest): Promise<void> | null {
    const view = resolveSymbolView(request.cellNode);
    if (!view) return null;
    // No queueIdleAfter — this clip ends with the symbol gone, and the caller releases the view
    // straight after (the release hook detaches the host and resets it to setup pose).
    const seconds = this.host.play(view, request.symbolId, this.clipName);
    if (seconds <= 0) return null; // e.g. chance/freespins carry no `out` → sprite shrink + burst
    // Scoped to the caller's removal beat so a slam fast-forwards the wait.
    return this.taskRunner.sleep(seconds, request.scope);
  }
}
